import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { CheckIcon,ExclamationCircleIcon } from '@heroicons/react/24/outline';
import Select from 'react-select';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate, useParams,Link,useLocation } from 'react-router-dom';
import Breadcrumb from '../common/Breadcrumb';
import { useInvoices } from '../../context/InvoiceContext';
import { usePaymentTerms } from '../../context/PaymentTermContext';
import { useJobs } from '../../context/JobContext';
import { useAuth } from '../../context/AuthContext';
import { useLoading } from '../../context/LoadingContext';
import { useRoutePath } from '../../utils/getRoutePath';
 
// Validation schema
const schema = yup.object().shape({
  invoice_date: yup.string().required('Invoice date is required'),
  payment_term_id: yup.string().required('Payment terms is required'),
  active: yup.boolean(),
  job_id: yup.string().required('Job is required')
});

const InvoiceForm = () => {
  const getRoutePath = useRoutePath();
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const { addInvoice, updateInvoice, getInvoiceById } = useInvoices();
  const [isLoading, setIsLoading] = useState(false);
  const { showLoading, hideLoading } = useLoading();
  const { getPaymentTerms } = usePaymentTerms();
  const { getJobs,getJobById } = useJobs();
  const [paymentTerms, setPaymentTerms] = useState([]);
  const [jobs, setJobs] = useState([]);
  const dataLoadedRef = useRef(false);
  const hasFetched = useRef(false);
  const [getEditData, setGetEditData] = useState(null);
  const location = useLocation();


  // Add custom styles for react-select
  const customStyles = {
    option: (provided, state) => ({
      ...provided,
      opacity: state.data.value === '' ? 0.5 : 1,
      fontStyle: state.data.value === '' ? 'normal' : 'normal',
    }),
    singleValue: (provided, state) => ({
      ...provided,
      opacity: state.data.value === '' ? 0.5 : 1,
      fontStyle: state.data.value === '' ? 'normal' : 'normal',
    }),
  };

  const getDefaultValues = useCallback((existingData = null) => {
  
    const data = existingData || {};
    return {
      invoice_date: data?.invoice_date || '',
      payment_term_id: data?.payment_term_id || '',
      active: data?.active ?? true,
      job_id: data?.job_id || '',
      job_date: data?.job_date || '',
      job_number: data?.job_number || '',
      po_number: data?.po_number || '',
      description: data?.description || '',
      location: data?.location || '',
      job_total: data?.job?.job_total || 0,
      job_deposits: data?.job?.total_payments_deposits_cost || 0,
      job_total_due: data?.job?.total_due || 0
    };
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    setError,
    watch,
    reset,
    trigger
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: getDefaultValues(getEditData),
    mode: 'onChange'
  });
  

  const handleSelectedJob = useCallback((selectedJob) => {
 
    setValue('job_id', selectedJob?.id || '');
    setValue('job_date', selectedJob?.formated_created_at || '');
    setValue('job_number', selectedJob?.job_number || '');
    setValue('po_number', selectedJob?.po_number || '');
    setValue('description', selectedJob?.description || '');
    setValue('location', selectedJob?.customer?.service_locations?.[0]?.street_address || '');
    setValue('job_total', selectedJob?.job_total || 0);
    setValue('job_deposits', selectedJob?.total_payments_deposits_cost || 0);
    setValue('job_total_due', selectedJob?.total_due || 0);
  }, [setValue]);


  const handleJobSelect = useCallback(async (selected) => {
    // Determine the jobId based on input type
    const jobId = typeof selected === 'object' ? selected.value : selected;
  
    // Clear previous job fields
 
    if (jobId) {
      const fetchJobById = await getJobById(jobId);
     
      const selectedJob = fetchJobById?.data;
      //const selectedJob = jobs.find(job => job.id === jobId);
      if (selectedJob) {
        handleSelectedJob(selectedJob);
      }
    }else{
        handleSelectedJob(null);
    }
  
    // Trigger validation for job_id
    trigger('job_id');
  }, [  handleSelectedJob, trigger, getJobById]);
 
  useEffect(() => {
    const loadJob = async () => {
      const params = new URLSearchParams(location.search);
      const jobId = params.get("jobId");
      if(jobId){
        let filters = {}
        filters.active = 1;
        const jobsData = await getJobs(0,filters);
        if(jobsData?.data){
          let job = jobsData?.data?.find(job => job.job_number === jobId);
          if(job){
          
            handleSelectedJob(job);
           
          }
        }
         
      }
    }
    loadJob();
   
  }, [location.search,handleSelectedJob,getJobs]);

  // Reset form when not in edit mode and set default job values
  useEffect(() => {
    if (!id) {
      const defaultValues = getDefaultValues();
      reset(defaultValues);
      // Set default values for job fields
      handleSelectedJob(null);
    }
  }, [id, reset, getDefaultValues,handleSelectedJob]);

  const fetchInvoiceData = useCallback(async () => {
    if (id && !hasFetched.current) {
     
      hasFetched.current = true;
      showLoading();
      try {
        const response = await getInvoiceById(id);
       
        if (response?.status === 200 && response?.data) {
          setGetEditData(response.data);
          // Reset form with fetched data
          reset(getDefaultValues(response.data));
        }
      } catch (error) {
        console.error('Error fetching invoice data:', error);
        toast.error('Error fetching invoice data');
      } finally {
        hideLoading();
      }
    }
  }, [id, getInvoiceById, showLoading, hideLoading, reset, getDefaultValues]);

  useEffect(() => {
    fetchInvoiceData();
  }, [fetchInvoiceData]);

  // Load invoice data, payment terms and jobs
  useEffect(() => {
    
    const loadData = async () => {
      try {
        // Load payment terms
        let filters = {}
        filters.active = 1;
        const paymentTermsData = await getPaymentTerms(0,filters);
        setPaymentTerms(paymentTermsData?.data || []);

        filters.is_invoiced = '0';
        // Load jobs
        const jobsData = await getJobs(0,filters);
       
        setJobs(jobsData?.data || []);
 
        
      } catch (error) {
        console.error('Error loading data:', error);
        toast.error('Failed to load form data');
      }
    };

    if(dataLoadedRef.current === false){
        loadData();
        dataLoadedRef.current = true;
    }
   
  }, [id, navigate, getPaymentTerms, getJobs, user, getRoutePath]);
  
   
  const jobId = watch('job_id'); // extract outside

  useEffect(() => {
    
    if (jobId && id) {
        
      handleJobSelect(jobId); // runs whenever jobId changes
    }  
  }, [jobId, handleJobSelect,id]); // now lint is happy

  // Add this function to handle payment terms change
  const handlePaymentTermsChange = (selectedOption) => {
    setValue('payment_term_id', selectedOption?.value || '');
    trigger('payment_term_id'); // Trigger validation after value change
  };

  const onSubmit = async (data) => {
    setIsLoading(true);
    showLoading();
    try {
      if (id) {
        // Update existing invoice
        const response = await updateInvoice(id, data);
        
        if (response.status === 200) {
          toast.success('Invoice updated successfully!');
          reset();
          navigate(getRoutePath('/invoices'));
        } else {
          if (response.errors) {
            Object.entries(response.errors).forEach(([field, message]) => {
              setError(field, {
                type: 'manual',
                message,
              });
            });
          } else if (response.error?.message) {
            setError('general', {
              type: 'manual',
              message: response.error.message,
            });
          }
        }
        setIsLoading(false);
        hideLoading();
      } else {
        // Create new invoice
        const response = await addInvoice(data);
        if (response.status === 200) {
          toast.success('Invoice created successfully!');
          reset();
          navigate(getRoutePath('/invoices'));
        } else {
          if (response.errors) {
            Object.entries(response.errors).forEach(([field, message]) => {
              setError(field, {
                type: 'manual',
                message,
              });
            });
          } else if (response.error?.message) {
            setError('general', {
              type: 'manual',
              message: response.error.message,
            });
          }
        }
        
        setIsLoading(false);
        hideLoading();
      }
    } catch (error) {
      setIsLoading(false);
      hideLoading();
      console.error('Error saving invoice:', error);
      toast.error('Failed to save invoice. Please try again.', {
        position: "top-right",
        autoClose: 5000,
      });
    }
  };
 
  return (
    
    <div className="container mx-auto px-4 py-8">
      <Breadcrumb />
      
      <div className="mx-auto">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">
          {id ? 'Edit Invoice' : 'Create New Invoice'}
        </h1>
      </div>
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Invoice Information</h2>
        
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" autoComplete="off">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Invoice Details */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Invoice Date <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  {...register('invoice_date')}
                  className={`datepicker appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm ${
                    errors.invoice_date ? 'border-red-500' : ''
                  }`}
                  onInput={
                    (e) => {
                      setValue('invoice_date', e.target.value);
                      trigger('invoice_date');
                    }
                  }
                  placeholder="Select Invoice Date"
                />
                {errors.invoice_date && (
                  <p className="mt-1 text-sm text-red-600">{errors.invoice_date.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Payment Terms <span className="text-red-500">*</span></label>
               
                <Select
                  name="payment_term_id"
                  isSearchable={true}
                  value={paymentTerms
                    ?.map(term => ({ value: term.id, label: term.name }))
                    .find(option => option.value === Number(watch('payment_term_id')))}
                  onChange={handlePaymentTermsChange}
                  options={[
                    { value: '', label: 'Choose Payment Terms' },
                    ...paymentTerms.map(term => ({
                      value: term.id,
                      label: term.name
                    }))
                  ]}
                  styles={{
                    ...customStyles,
                    control: (base, state) => ({
                      ...base,
                      minHeight: '42px',
                      borderColor: errors.payment_term_id ? '#ef4444' : '#d1d5db',
                      '&:hover': {
                        borderColor: errors.payment_term_id ? '#ef4444' : '#9ca3af'
                      }
                    })
                  }}
                  placeholder="Select Payment Terms"
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
                {errors.payment_term_id && (
                  <p className="mt-1 text-sm text-red-600">{errors.payment_term_id.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Active</label>
                <input
                  type="checkbox"
                  {...register('active')}
                  className="form-checkbox h-5 w-5 text-blue-600"
                />
                {errors.active && (
                  <p className="mt-1 text-sm text-red-600">{errors.active.message}</p>
                )}
              </div>
            </div>
          </div>

           {/* Job Section */}
           <div className="space-y-4">
             <h3 className="text-lg font-medium text-gray-900">Job Information</h3>
              
             {!location.search.includes('jobId') && (
               <>
             {!id && (
              <>
              {/* Job Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700">Select Job <span className="text-red-500">*</span></label>
                
                <Select
                  name="job_id"
                  isSearchable={true}
                  value={jobs
                    ?.map(job => ({ value: job.id, label: `${job.job_number}` }))
                    .find(option => option.value === watch('job_id'))}
                  onChange={handleJobSelect}
                  
                  options={[
                    { value: '', label: 'Choose Job' },
                    ...jobs.map(job => ({
                      value: job.id,
                      label: `${job.job_number}`
                    }))
                  ]}
                  styles={{
                    ...customStyles,
                    control: (base, state) => ({
                      ...base,
                      minHeight: '42px',
                      borderColor: errors.job_id ? '#ef4444' : '#d1d5db',
                      '&:hover': {
                        borderColor: errors.job_id ? '#ef4444' : '#9ca3af'
                      }
                    })
                  }}
                  placeholder="Select Job"
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
                {errors.job_id && (
                  <p className="mt-1 text-sm text-red-600">{errors.job_id.message}</p>
                )}
              </div>
              </>
             )}
             </>
            )}
 
            {watch('job_id') && (
                <>
                {/* Job Details Table */}
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
                    <h4 className="text-lg font-medium text-gray-900">Job Details</h4>
                </div>
                
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Job Date</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Job#</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">PO#</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Job Total</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Job Deposits</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Job Total Due</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        <tr>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {watch('job_date') || '-'}
                            <input type="hidden" {...register('job_date')} />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                           <Link to={getRoutePath(`/jobs/view/${watch('job_id')}`)} className="text-[#009efb] hover:text-[#0089db]" target="_blank"> {watch('job_number') || '-'}</Link>
                            <input type="hidden" {...register('job_number')} />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {watch('po_number') || '-'}
                            <input type="hidden" {...register('po_number')} />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {watch('description') || '-'}
                            <input type="hidden" {...register('description')} />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {watch('location') || '-'}
                            <input type="hidden" {...register('location')} />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            ${watch('job_total') ? parseFloat(watch('job_total')).toFixed(2) : '0.00'}
                            <input type="hidden" {...register('job_total')} />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            ${watch('job_deposits') ? parseFloat(watch('job_deposits')).toFixed(2) : '0.00'}
                            <input type="hidden" {...register('job_deposits')} />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 bg-blue-50">
                            ${watch('job_total_due') ? parseFloat(watch('job_total_due')).toFixed(2) : '0.00'}
                            <input type="hidden" {...register('job_total_due')} />
                        </td>
                        </tr>
                    </tbody>
                    </table>
                </div>
                </div>
                </>
             )}
           </div>

           {errors?.job_id && (
            <div className="mt-2 flex items-center gap-2 rounded-md border border-red-400 bg-red-100 px-3 py-2 text-sm text-red-700">
              <ExclamationCircleIcon className="w-5 h-5 flex-shrink-0" />
              <span>{errors?.job_id?.message}</span>
            </div>
          )}

          {/* Submit Button */}
          <div className="flex justify-end">
            <button
              type="submit"
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#009efb] hover:bg-[#0089db] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#009efb] transition-colors duration-200"
              disabled={isLoading}
            >
              {isLoading ? (
                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : (
                <>
                  <CheckIcon className="w-4 h-4 mr-2" />
                  {id ? 'Update' : 'Save'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
 
export default InvoiceForm;
